import { uploadFile } from "@/api/file";
import { formatPriceWithCommas, normalizePrice } from "@/utils/priceFormat";
import { calculateReplyProductMaterialPriceTexts } from "@shared/replyProductPrice.cjs";
import StoreProductPicker from "@/components/estimates/StoreProductPicker.vue";

export default {
  name: "ProductQuoteCards",
  components: { StoreProductPicker },
  props: {
    products: { type: Array, default: () => [] },
    editable: { type: Boolean, default: false },
    similarProductMode: { type: Boolean, default: false },
  },
  computed: {
    quoteGuide() {
      if (!this.similarProductMode) {
        return "고객이 선택하거나 첨부한 사진마다 견적가와 상세 정보를 입력해주세요.";
      }
      return this.editable
        ? "매장 제품을 찾아 선택하거나 직접 추가한 뒤 견적가와 상세 정보를 입력해주세요."
        : "고객 요청을 참고해 파트너사가 직접 제안한 비슷한 제품의 견적 정보입니다.";
    },
  },
  data() {
    return {
      colorOptions: [
        { value: "white", label: "화이트 컬러" },
        { value: "rose", label: "로즈골드" },
        { value: "yellow", label: "옐로우 컬러" },
      ],
      diamondTypes: ["천연", "랩그로운"],
      diamondColors: "DEFGHIJKLMNOPQRSTUVWXYZ".split(""),
      diamondClarities: [
        "FL",
        "IF",
        "VVS1",
        "VVS2",
        "VS1",
        "VS2",
        "SI1",
        "SI2",
        "I1",
        "I2",
      ],
      diamondCuts: ["Excellent", "Very Good", "Good", "Fair"],
      diamondDialog: { visible: false, productId: "", diamondId: "", form: {} },
      uploadingProductId: "",
      uploadTarget: null,
      dragProductId: "",
      selectedImages: {},
      storeProductPicker: { visible: false, targetId: "" },
      sequence: 0,
    };
  },
  methods: {
    openStoreProductPicker(targetId = "") {
      if (!this.editable || this.uploadingProductId) return;
      if (!targetId && this.products.length >= 10) return;
      this.storeProductPicker = { visible: true, targetId };
    },
    selectStoreProduct(source) {
      if (!this.editable || this.uploadingProductId) return;
      const imageUrls = [
        ...new Set(
          (source.imagesUrls || [])
            .filter((image) => typeof image === "string" && image.trim())
            .map((image) => image.trim())
        ),
      ].slice(0, 5);
      if (!source._id || !imageUrls.length) return;
      const targetId = this.storeProductPicker.targetId;
      if (
        this.products.some(
          (product) =>
            product.productId === source._id && product.id !== targetId
        )
      ) {
        this.$message.error("이미 견적에 추가한 상품입니다.");
        return;
      }
      const selection = {
        productId: source._id,
        name: (source.name || "").slice(0, 120),
        imageUrl: imageUrls[0],
        imageUrls,
      };
      if (targetId) {
        this.updateProduct(targetId, (product) =>
          Object.assign(product, selection)
        );
        this.$set(this.selectedImages, targetId, "");
      } else {
        if (this.products.length >= 10) return;
        this.addProduct(selection);
      }
      this.storeProductPicker.visible = false;
    },
    newId(prefix) {
      this.sequence += 1;
      return prefix + "-" + Date.now() + "-" + this.sequence;
    },
    emitProducts(products) {
      if (this.editable) this.$emit("update-products", products);
    },
    cloneProduct(product) {
      return {
        ...product,
        material: { ...product.material },
        promotion: { ...(product.promotion || { enabled: false, price: "" }) },
        imageUrls: product.imageUrls.slice(),
        diamonds: product.diamonds.map((item) => ({ ...item })),
      };
    },
    updateProduct(id, update) {
      if (!this.editable) return;
      const index = this.products.findIndex((product) => product.id === id);
      if (index < 0) return;
      const next = this.products.slice();
      next[index] = this.cloneProduct(next[index]);
      update(next[index]);
      this.emitProducts(next);
    },
    syncMoneyInputs() {
      this.$nextTick(() =>
        (this.$refs.moneyInputs || []).forEach((input) =>
          input.setNativeInputValue()
        )
      );
    },
    setProductField(id, key, value) {
      this.updateProduct(id, (product) => {
        product[key] = key === "price" ? normalizePrice(value) : value;
      });
      if (key === "price") this.syncMoneyInputs();
    },
    selectMaterial(id, base) {
      this.updateProduct(id, (product) => {
        product.material.base = base;
      });
    },
    setMaterialPrice(id, value) {
      this.updateProduct(id, (product) => {
        const inputText = String(value || "");
        if (/[^\d,\s원]/.test(inputText)) {
          product.material.additionalPrice = "";
          return;
        }
        const normalized = normalizePrice(inputText);
        product.material.additionalPrice =
          normalized || (/\d/.test(inputText) ? "0" : "");
      });
      this.syncMoneyInputs();
    },
    displayPrice(value) {
      return value === "0" ? "0" : formatPriceWithCommas(value);
    },
    setProductPromotion(id, key, value) {
      this.updateProduct(id, (product) => {
        product.promotion[key] =
          key === "price" ? normalizePrice(value) : value === true;
        if (!product.promotion.enabled) product.promotion.price = "";
      });
      if (key === "price") this.syncMoneyInputs();
    },
    productPromotionStatus(product) {
      const promotion = product.promotion || {};
      if (!promotion.customerViewedAt) return "고객 확인 전";
      if (
        promotion.expiresAt &&
        new Date(promotion.expiresAt).getTime() <= Date.now()
      )
        return "혜택 종료";
      return "고객 확인 후 24시간 혜택 진행 중";
    },
    hasSavedMaterialPriceTexts(product) {
      const material = product && product.material;
      return Boolean(
        (product && product.minimumPriceText) ||
          (material && (material.price14KText || material.price18KText))
      );
    },
    materialPriceTexts(product) {
      const material = product && product.material;
      if (!this.editable) {
        return {
          minimumPriceText: (product && product.minimumPriceText) || "",
          price14KText: (material && material.price14KText) || "",
          price18KText: (material && material.price18KText) || "",
        };
      }
      const calculated = calculateReplyProductMaterialPriceTexts(product);
      return (
        calculated || {
          minimumPriceText: "",
          price14KText: "",
          price18KText: "",
        }
      );
    },
    addProduct(selection = {}) {
      if (
        !this.editable ||
        this.products.length >= 10 ||
        this.uploadingProductId
      )
        return;
      this.emitProducts(
        this.products.concat([
          {
            id: this.newId("draft"),
            productId: "",
            name: "",
            description: "",
            imageUrl: "",
            imageUrls: [],
            price: "",
            material: { base: "14K", additionalPrice: "" },
            color: "white",
            diamonds: [],
            promotion: { enabled: false, price: "" },
            ...selection,
          },
        ])
      );
    },
    removeProduct(id) {
      if (!this.uploadingProductId)
        this.emitProducts(this.products.filter((product) => product.id !== id));
    },
    moveProduct(index, offset) {
      const target = index + offset;
      if (
        this.uploadingProductId ||
        target < 0 ||
        target >= this.products.length
      )
        return;
      const next = this.products.slice();
      next.splice(target, 0, next.splice(index, 1)[0]);
      this.emitProducts(next);
    },
    dropProduct(id) {
      const from = this.products.findIndex(
        (product) => product.id === this.dragProductId
      );
      const to = this.products.findIndex((product) => product.id === id);
      if (from >= 0 && to >= 0) this.moveProduct(from, to - from);
      this.dragProductId = "";
    },
    shownImage(product) {
      return this.selectedImages[product.id] || product.imageUrl;
    },
    selectImage(product, image) {
      if (this.editable) this.setProductField(product.id, "imageUrl", image);
      else this.$set(this.selectedImages, product.id, image);
    },
    removeSelectedImage(id) {
      this.updateProduct(id, (product) => {
        product.imageUrls = product.imageUrls.filter(
          (image) => image !== product.imageUrl
        );
        product.imageUrl = product.imageUrls[0] || "";
        if (!product.imageUrls.length) product.productId = "";
      });
    },
    addDiamond(product) {
      if (!this.editable || product.diamonds.length >= 10) return;
      this.diamondDialog = {
        visible: true,
        productId: product.id,
        diamondId: "",
        form: {
          name: "",
          carat: "",
          color: "D",
          clarity: "VVS1",
          cut: "Excellent",
        },
      };
    },
    editDiamond(productId, diamond) {
      if (this.editable)
        this.diamondDialog = {
          visible: true,
          productId,
          diamondId: diamond.id,
          form: {
            ...diamond,
            name: this.diamondTypes.includes(diamond.name) ? diamond.name : "",
          },
        };
    },
    saveDiamond() {
      const dialog = this.diamondDialog;
      const form = dialog.form;
      const carat = String(form.carat || "").trim();
      if (
        !this.diamondTypes.includes(form.name) ||
        !/^\d{1,3}(?:\.\d{1,3})?$/.test(carat) ||
        Number(carat) <= 0 ||
        Number(carat) > 100 ||
        !this.diamondColors.includes(form.color) ||
        !this.diamondClarities.includes(form.clarity) ||
        !this.diamondCuts.includes(form.cut)
      ) {
        this.$message.error(
          "다이아몬드 종류와 올바른 등급 정보를 입력해주세요. 캐럿은 0 초과, 100 이하입니다."
        );
        return;
      }
      this.updateProduct(dialog.productId, (product) => {
        const diamond = {
          ...form,
          id: dialog.diamondId || this.newId("diamond"),
          name: form.name,
          carat,
        };
        if (dialog.diamondId) {
          const index = product.diamonds.findIndex(
            (item) => item.id === dialog.diamondId
          );
          if (index >= 0) product.diamonds.splice(index, 1, diamond);
        } else if (product.diamonds.length < 10) product.diamonds.push(diamond);
      });
      this.diamondDialog.visible = false;
    },
    removeDiamond(productId, diamondId) {
      this.updateProduct(productId, (product) => {
        product.diamonds = product.diamonds.filter(
          (diamond) => diamond.id !== diamondId
        );
      });
    },
    openImagePicker(id, mode) {
      if (!this.editable || this.uploadingProductId) return;
      const product = this.products.find((item) => item.id === id);
      if (!product || (mode === "append" && product.imageUrls.length >= 5))
        return;
      this.uploadTarget = { id, mode, image: product.imageUrl };
      const input = (this.$refs.productImageInputs || []).find(
        (item) => item.dataset.productId === id
      );
      if (input) input.click();
    },
    async uploadProductImage(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      const target = this.uploadTarget;
      if (!this.editable || !file || !target || this.uploadingProductId) return;
      if (
        !/\.(jpe?g|png|gif|webp|svg)$/i.test(file.name) ||
        !/^image\/(jpeg|png|gif|webp|svg\+xml)$/.test(file.type) ||
        file.size > 30 * 1024 * 1024
      ) {
        this.$message.error(
          "JPG, PNG, GIF, WEBP, SVG 이미지를 30MB 이하로 등록해주세요."
        );
        input.value = "";
        return;
      }
      this.uploadingProductId = target.id;
      this.$emit("uploading", true);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await uploadFile(formData);
        if (res.data.status !== 200 || !/^https?:\/\//.test(res.data.url || ""))
          throw new Error("Image upload failed");
        this.updateProduct(target.id, (product) => {
          const index =
            target.mode === "replace"
              ? product.imageUrls.indexOf(target.image)
              : -1;
          if (index >= 0) product.imageUrls.splice(index, 1, res.data.url);
          else if (product.imageUrls.length < 5)
            product.imageUrls.push(res.data.url);
          product.imageUrls = Array.from(new Set(product.imageUrls));
          if (product.imageUrls.includes(res.data.url))
            product.imageUrl = res.data.url;
        });
      } catch (error) {
        this.$message.error("이미지 업로드에 실패했습니다. 다시 시도해주세요.");
      } finally {
        this.uploadingProductId = "";
        this.uploadTarget = null;
        input.value = "";
        this.$emit("uploading", false);
      }
    },
  },
};
